import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useMessaging } from "../../context/MessagingContext";
import { useRecipientList } from "../../context/RecipientListContext";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "../../context/UserRoleContext";

const NewMessageDropdown = ({ isOpen, onClose }) => {
  const { getOrCreateConversation, createGroupConversation } = useMessaging();
  const { recipients, loading, error, getRecipients } = useRecipientList();
  const { userData } = useUserRole();
  const navigate = useNavigate();
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const closeModal = useCallback(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest(".new-message-dropdown")) {
      active.blur();
    }
    onClose();
  }, [onClose]);

  const recipientsList = recipients || [];

  const filteredRecipients = useMemo(() => {
    if (!searchTerm.trim()) return recipientsList;
    const term = searchTerm.toLowerCase();
    return recipientsList.filter((recipient) => {
      const matchesName = recipient.name?.toLowerCase().includes(term);
      const matchesRole = recipient.role?.toLowerCase().includes(term);
      const matchesRelationships =
        recipient.relationships?.some(
          (r) =>
            r.artist_name?.toLowerCase().includes(term) ||
            r.bar_name?.toLowerCase().includes(term)
        );
      return matchesName || matchesRole || matchesRelationships;
    });
  }, [searchTerm, recipientsList]);

  useEffect(() => {
    if (isOpen) {
      getRecipients(false);
      setSelectedRecipients([]);
      setGroupName("");
      setStartError(null);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSearchTerm("");
    }
  }, [isOpen, getRecipients]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, closeModal]);


  const toggleRecipient = (recipient) => {
    const isSelected = selectedRecipients.some(
      (r) => r.user_id === recipient.user_id,
    );
    if (isSelected) {
      setSelectedRecipients((prev) =>
        prev.filter((r) => r.user_id !== recipient.user_id),
      );
    } else {
      setSelectedRecipients((prev) => [...prev, recipient]);
    }
  };

  const removeSelected = (userId) => {
    setSelectedRecipients((prev) => prev.filter((r) => r.user_id !== userId));
  };

  const handleStartChat = async () => {
    if (selectedRecipients.length === 0) return;

    try {
      setStarting(true);
      setStartError(null);

      const basePath =
        userData?.role === "artist"
          ? "/artist"
          : userData?.role === "bar_owner"
            ? "/bar-owner"
            : "/bar-admin";

      if (selectedRecipients.length === 1) {
        const conversation = await getOrCreateConversation(
          selectedRecipients[0].user_id,
        );
        if (conversation) {
          closeModal();
          navigate(
            `${basePath}/messages?conversation=${conversation.conversation_id}`,
          );
        }
      } else {
        const participantIds = selectedRecipients.map((r) => r.user_id);
        const conversation = await createGroupConversation(
          participantIds,
          groupName.trim() || null,
        );
        if (conversation) {
          closeModal();
          navigate(
            `${basePath}/messages?conversation=${conversation.conversation_id}`,
          );
        }
      }
    } catch (err) {
      console.error("Error starting conversation:", err);
      setStartError(err.message || "Failed to start conversation");
    } finally {
      setStarting(false);
    }
  };

  const formatRelationships = (recipient) => {
    const rels = recipient.relationships;
    if (!rels || rels.length === 0) {
      switch (recipient.role) {
        case "bar_owner":
          return <span className="recipient-source-badge bar-owner">Owner</span>;
        case "bar_admin":
          return <span className="recipient-source-badge bar-admin">Admin</span>;
        case "artist":
          return <span className="recipient-source-badge artist">Artist</span>;
        default:
          return null;
      }
    }

    const bySource = {};
    rels.forEach((r) => {
      if (!bySource[r.source]) bySource[r.source] = [];
      const name = r.bar_name || r.artist_name;
      if (name && !bySource[r.source].includes(name)) {
        bySource[r.source].push(name);
      }
    });

    const parts = [];
    if (bySource.band_member?.length) {
      parts.push(
        <span key="bm" className="recipient-source-badge band-member">
          Band mate • {bySource.band_member.join(", ")}
        </span>
      );
    }
    if (bySource.bar_owner?.length) {
      parts.push(
        <span key="bo" className="recipient-source-badge bar-owner">
          Bar owner • {bySource.bar_owner.join(", ")}
        </span>
      );
    }
    if (bySource.bar_admin?.length) {
      parts.push(
        <span key="ba" className="recipient-source-badge bar-admin">
          Bar admin • {bySource.bar_admin.join(", ")}
        </span>
      );
    }
    if (bySource.artist?.length) {
      parts.push(
        <span key="ar" className="recipient-source-badge artist">
          Artist • {bySource.artist.join(", ")}
        </span>
      );
    }

    if (parts.length === 0) return null;
    return (
      <div className="recipient-relationships">
        {parts.reduce(
          (acc, part, i) => (i === 0 ? [part] : [...acc, " | ", part]),
          []
        )}
      </div>
    );
  };

  if (!isOpen) {
    return null;
  }

  const selectedIds = new Set(selectedRecipients.map((r) => r.user_id));
  const canStart = selectedRecipients.length > 0;

  return (
    <div className="new-message-dropdown" ref={dropdownRef}>
      <div className="new-message-dropdown-header">
        <h3>New Message</h3>
        <button
          type="button"
          className="new-message-dropdown-close"
          onClick={closeModal}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {selectedRecipients.length > 0 && (
        <div className="new-message-selected">
          <div className="selected-chips">
            {selectedRecipients.map((r) => (
              <span key={r.user_id} className="selected-chip">
                {r.name}
                <button
                  type="button"
                  className="selected-chip-remove"
                  onClick={() => removeSelected(r.user_id)}
                  aria-label={`Remove ${r.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {selectedRecipients.length >= 2 && (
            <input
              type="text"
              className="new-message-group-name"
              placeholder="Group name (optional)"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="new-message-dropdown-search">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search recipients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="new-message-search-input"
        />
      </div>

      <div className="new-message-dropdown-list">
        {loading && recipientsList.length === 0 ? (
          <div className="new-message-loading">
            <div className="spinner"></div>
            <p>Loading recipients...</p>
          </div>
        ) : error ? (
          <div className="new-message-error">
            <p>{error}</p>
            <button
              type="button"
              onClick={() => getRecipients(false)}
              className="new-message-retry-btn"
            >
              Retry
            </button>
          </div>
        ) : filteredRecipients.length === 0 ? (
          <div className="new-message-empty">
            <p>
              {searchTerm ? "No recipients found" : "No recipients available"}
            </p>
          </div>
        ) : (
          filteredRecipients.map((recipient) => {
            const isSelected = selectedIds.has(recipient.user_id);
            const relationshipsDisplay = formatRelationships(recipient);
            return (
              <div
                key={recipient.user_id}
                className={`new-message-recipient-item ${isSelected ? "selected" : ""}`}
                onClick={() => toggleRecipient(recipient)}
              >
                <div className="recipient-avatar">
                  {recipient.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="recipient-info">
                  <div className="recipient-name-row">
                    <span className="recipient-name">{recipient.name}</span>
                  </div>
                  {relationshipsDisplay && (
                    <div className="recipient-relationship">
                      {relationshipsDisplay}
                    </div>
                  )}
                </div>
                {isSelected && <span className="recipient-check">✓</span>}
              </div>
            );
          })
        )}
      </div>

      {startError && (
        <div className="new-message-error">
          <p>{startError}</p>
          <div className="new-message-error-actions">
            <button
              type="button"
              onClick={() => setStartError(null)}
              className="new-message-retry-btn"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={() => {
                setStartError(null);
                handleStartChat();
              }}
              className="new-message-retry-btn"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="new-message-dropdown-footer">
        <button
          type="button"
          className="new-message-cancel-btn"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          type="button"
          className="new-message-start-btn"
          onClick={handleStartChat}
          disabled={!canStart || starting}
        >
          {starting
            ? "Starting..."
            : selectedRecipients.length === 1
              ? "Start chat"
              : selectedRecipients.length > 1
                ? "Start group chat"
                : "Select recipients"}
        </button>
      </div>
    </div>
  );
};

export default NewMessageDropdown;
